import { booleanAttribute, ChangeDetectionStrategy, Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'tran-checkbox-input',
    imports: [ReactiveFormsModule, TranslatePipe],
    templateUrl: './checkbox-input.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CheckboxInputComponent),
            multi: true
        }
    ]
})
export class CheckboxInputComponent implements ControlValueAccessor {
    public readonly label = input.required<string>();
    public readonly placeholder = input<string>('');
    public readonly readonly = input(false, { transform: booleanAttribute });

    protected readonly isChecked = signal<boolean>(false);
    protected readonly inputControl = new FormControl<string>('');
    protected readonly isDisabled = signal<boolean>(false);

    private onChange?: (value: string | null) => void;
    private onTouched?: () => void;

    writeValue(value: string | null): void {
        if (value !== null && value !== '') {
            this.isChecked.set(true);
            this.inputControl.setValue(value, { emitEvent: false });
        } else {
            this.isChecked.set(false);
            this.inputControl.setValue('', { emitEvent: false });
        }
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    protected onCheckboxChange(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.isChecked.set(checked);

        if (!checked) {
            this.inputControl.setValue('');
        }

        this.emitValue();
        this.onTouched?.();
    }

    protected onInputChange(): void {
        this.emitValue();
        this.onTouched?.();
    }

    private emitValue(): void {
        const value = this.isChecked() && this.inputControl.value ? this.inputControl.value : null;
        this.onChange?.(value);
    }
}
